// ─────────────────────────────────────────────────────────────────────────────
// Professional Excel export helper (ExcelJS)
//
// The free SheetJS (`xlsx`) build silently ignores cell styles, so all our old
// header colouring never actually rendered. ExcelJS supports fills, fonts,
// borders and number formats in its free build — this module centralises a
// consistent, professional look (coloured headers, banded rows, totals) plus
// label formatting (Title Case for priority / status / type).
// ─────────────────────────────────────────────────────────────────────────────
import ExcelJS from 'exceljs';

// Brand palette
export const COLORS = {
  headerBg:   'FF1E293B', // dark navy
  headerText: 'FFFFFFFF',
  bandBg:     'FFF1F5F9', // very light slate (zebra striping)
  totalBg:    'FFE2E8F0', // light slate for totals
  border:     'FFCBD5E1',
  accent:     'FF5B5BD6', // brand violet
};

// Turn a snake_case / lowercase token into a readable Title Case label.
// e.g. "in_progress" -> "In Progress", "urgent" -> "Urgent", "milestone" -> "Milestone"
export function prettyLabel(v) {
  if (v === null || v === undefined || v === '') return '';
  return String(v)
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function applyHeaderStyle(row) {
  row.eachCell(cell => {
    cell.font      = { bold: true, color: { argb: COLORS.headerText }, size: 11 };
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border    = {
      top:    { style: 'thin', color: { argb: COLORS.border } },
      bottom: { style: 'thin', color: { argb: COLORS.border } },
      left:   { style: 'thin', color: { argb: COLORS.border } },
      right:  { style: 'thin', color: { argb: COLORS.border } },
    };
  });
  row.height = 22;
}

function applyBodyCell(cell, banded) {
  cell.font   = { size: 10, color: { argb: 'FF1E293B' } };
  cell.border = {
    top:    { style: 'hair', color: { argb: COLORS.border } },
    bottom: { style: 'hair', color: { argb: COLORS.border } },
    left:   { style: 'hair', color: { argb: COLORS.border } },
    right:  { style: 'hair', color: { argb: COLORS.border } },
  };
  if (banded) {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.bandBg } };
  }
  if (!cell.alignment) cell.alignment = { vertical: 'top', wrapText: true };
}

/**
 * Build and download a styled .xlsx workbook.
 *
 * sheets: [{
 *   name: 'Tasks',
 *   columns: [{ header:'Task Name', key:'name', width:40, align?, numFmt? }, ...],
 *   rows: [ { name:'…', … }, … ],            // objects keyed by column.key
 *   totalRow?: { name:'TOTAL', hours: 42 },  // optional bold total row
 *   title?: 'Optional report title',         // optional banner above the header
 *   subtitle?: 'Generated …',
 * }]
 */
export async function downloadStyledWorkbook(filename, sheets) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Enterprise Operations Management Platform (EOMP)';
  wb.created = new Date();

  for (const sheet of sheets) {
    const ws = wb.addWorksheet(sheet.name || 'Sheet', {
      views: [{ state: 'frozen', ySplit: sheet.title ? 3 : 1 }],
    });

    const cols = sheet.columns || [];
    let headerRowNum = 1;

    // Optional title banner
    if (sheet.title) {
      ws.mergeCells(1, 1, 1, cols.length);
      const tcell = ws.getCell(1, 1);
      tcell.value = sheet.title;
      tcell.font = { bold: true, size: 15, color: { argb: COLORS.headerText } };
      tcell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
      tcell.alignment = { horizontal: 'left', vertical: 'middle' };
      ws.getRow(1).height = 28;

      ws.mergeCells(2, 1, 2, cols.length);
      const scell = ws.getCell(2, 1);
      scell.value = sheet.subtitle || '';
      scell.font = { italic: true, size: 10, color: { argb: 'FF64748B' } };
      scell.alignment = { horizontal: 'left', vertical: 'middle' };
      ws.getRow(2).height = 16;

      headerRowNum = 3;
    }

    // Column widths
    cols.forEach((c, i) => { ws.getColumn(i + 1).width = c.width || 16; });

    // Header row
    const headerRow = ws.getRow(headerRowNum);
    cols.forEach((c, i) => { headerRow.getCell(i + 1).value = c.header; });
    applyHeaderStyle(headerRow);

    // Body rows
    (sheet.rows || []).forEach((r, ri) => {
      const excelRow = ws.getRow(headerRowNum + 1 + ri);
      // A row can be a full-width section banner: { _section: 'Title' }
      if (r._section !== undefined) {
        ws.mergeCells(headerRowNum + 1 + ri, 1, headerRowNum + 1 + ri, cols.length);
        const cell = excelRow.getCell(1);
        cell.value = r._section;
        cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: r._sectionColor || COLORS.accent } };
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        excelRow.height = 20;
        return;
      }
      cols.forEach((c, ci) => {
        const cell = excelRow.getCell(ci + 1);
        cell.value = r[c.key] !== undefined && r[c.key] !== null ? r[c.key] : '';
        applyBodyCell(cell, ri % 2 === 1);
        if (c.numFmt) cell.numFmt = c.numFmt;
        // Per-cell number format override: row._numFmt[key] or a single row._numFmtValue.
        if (r._numFmt && r._numFmt[c.key]) cell.numFmt = r._numFmt[c.key];
        if (r._numFmtValue && c.key === 'value') cell.numFmt = r._numFmtValue;
        // Per-cell colour override: row._fill[key] / row._font[key] (ARGB)
        if (r._fill && r._fill[c.key]) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: r._fill[c.key] } };
        if (r._font && r._font[c.key]) cell.font = { size: 10, bold: true, color: { argb: r._font[c.key] } };
        // Per-cell border override: row._border[key] = ARGB (medium box border).
        // Used e.g. to mark a completed-but-critical task cell (green fill + red box).
        if (r._border && r._border[c.key]) {
          const bc = { style: 'medium', color: { argb: r._border[c.key] } };
          cell.border = { top: bc, bottom: bc, left: bc, right: bc };
        }
        cell.alignment = {
          horizontal: c.align || (typeof cell.value === 'number' ? 'right' : 'left'),
          vertical: 'top',
          wrapText: true,
        };
        if (r._indent && ci === 0) cell.alignment = { ...cell.alignment, indent: r._indent };
      });
    });

    // Optional total row
    if (sheet.totalRow) {
      const tr = ws.getRow(headerRowNum + 1 + (sheet.rows || []).length);
      cols.forEach((c, ci) => {
        const cell = tr.getCell(ci + 1);
        const v = sheet.totalRow[c.key];
        cell.value = v !== undefined && v !== null ? v : '';
        cell.font = { bold: true, size: 10, color: { argb: 'FF1E293B' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.totalBg } };
        cell.border = {
          top:    { style: 'double', color: { argb: COLORS.border } },
          bottom: { style: 'thin',   color: { argb: COLORS.border } },
        };
        if (c.numFmt) cell.numFmt = c.numFmt;
        cell.alignment = { horizontal: c.align || (typeof cell.value === 'number' ? 'right' : 'left') };
      });
    }

    // Auto-filter over the header
    ws.autoFilter = {
      from: { row: headerRowNum, column: 1 },
      to:   { row: headerRowNum, column: cols.length },
    };
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename.endsWith('.xlsx') ? filename : filename + '.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
